<template>
<div v-if="logs || showControls" class="logs-table-wrapper">
    <div v-if="showControls" class="table-toolbar">
      <div class="toolbar-left">
        <label>
          Tipo de operación:
          <select id="operation-type" v-model="operationType" @change="emitQuery">
            <option value="">Todas</option>
            <option value="CREATE">Creación</option>
            <option value="READ">Consulta</option>
            <option value="UPDATE">Actualización</option>
            <option value="DELETE">Eliminación</option>
            <option value="AUTH">Autenticación</option>
          </select>
        </label>
      </div>
      <div class="toolbar-right">
        <label>
          Buscar:
          <input
            type="text"
            v-model="search"
            @input="onSearchInput"
            placeholder="Buscar"
          />
        </label>
        <button
          v-if="hasActiveFilters"
          class="logs-clear-btn"
          type="button"
          @click="clearFilters"
        >
          Limpiar filtros
        </button>
      </div>
    </div>

    <div v-bind:class="{ 'logs-scroll': showControls }">
    <table v-if="safeLogs.length > 0 || showControls" class="logs-table">
      <thead>
        <tr>
          <template v-if="fullMode">
            <th v-if="showControls" class="sortable" @click="onSortDate">
              Fecha <font-awesome-icon :icon="sortIcon" />
            </th>
            <th v-else>Fecha</th>
            <th>Hora</th>
            <th>Usuario</th>
            <th>Acción</th>
            <th>Entidad</th>
            <th>ID recurso</th>
          </template>
          <template v-else>
            <th v-if="showControls" class="sortable" @click="onSortDate">
              Fecha y hora <font-awesome-icon :icon="sortIcon" />
            </th>
            <th v-else>Fecha y hora</th>
            <th>Acción</th>
          </template>
        </tr>
      </thead>
      <tbody>
        <tr
        v-for="(item, index) in safeLogs"
        v-bind:key="index"
        v-bind:class="{ 'row-clickable': showInfo, 'row-selected': isSelected(item.id) }"
        @click="onClickRow(item)">
          <template v-if="fullMode">
            <td class="log-date">{{dateFormatter(item.created_at)}}</td>
            <td class="log-date">{{timeFormatter(item.created_at)}}</td>
            <td>{{item.username || '—'}}</td>
            <td>{{item.action}}</td>
            <td>{{item.collection}}</td>
            <td>
              <span class="log-resource-id" v-bind:title="item.resource_id">{{item.resource_id}}</span>
            </td>
          </template>
          <template v-else>
            <td class="log-date">{{dateTimeFormatter(item.created_at)}}</td>
            <td>{{item.action}}</td>
          </template>
        </tr>
        <tr v-if="safeLogs.length === 0">
          <td class="logs-empty-cell" v-bind:colspan="columnCount">{{ emptyMessage }}</td>
        </tr>
      </tbody>
    </table>
    </div>
    <span v-if="safeLogs.length<=0 && !showControls">No hay registros</span>
</div>
</template>

<style lang="scss" scoped>
.logs-table-wrapper{
  overflow-x: auto;
}
//Scroll interno solo de las filas: toolbar y entorno quedan siempre a la vista
.logs-scroll{
  max-height: 65vh;
  overflow-y: auto;
}
.logs-scroll .logs-table thead th{
  position: sticky;
  top: 0;
  z-index: 1;
}
.table-toolbar{
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1em;
  margin-bottom: 1em;
}
.toolbar-left,
.toolbar-right{
  display: flex;
  align-items: center;
  gap: 0.75em;
}
.table-toolbar label{
  display: inline-flex;
  align-items: center;
  gap: 0.4em;
  font-size: 0.875em;
  white-space: nowrap;
}
.logs-clear-btn{
  padding: 0.3em 0.75em;
  border-radius: 4px;
  border: 1px solid #ccc;
  cursor: pointer;
  font-size: 0.85em;
  line-height: 1.4;
  background: transparent;
  color: #666;
  transition: opacity 0.15s;
  &:hover { color: #333; border-color: #999; }
}
.logs-table{
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 1em;
}
.logs-table th,
.logs-table td{
  padding: 0.6em 0.8em;
  border-bottom: 1px solid rgba(0, 0, 0, 0.15);
  text-align: left;
  vertical-align: middle;
  font-size: 0.875em;
}
.logs-table thead th{
  background: var(--color-navy);
  color: #fff;
  white-space: nowrap;
  font-weight: 600;
}
.logs-table th.sortable{
  cursor: pointer;
  user-select: none;
}
.logs-table th.sortable:hover{
  background: var(--color-steel);
}
.logs-table tbody tr:hover{
  background: rgba(59, 110, 148, 0.06);
}
.logs-table tbody tr.row-clickable{
  cursor: pointer;
}
.logs-table tbody tr.row-selected{
  background: #dbeeff;
}
.log-date{
  white-space: nowrap;
}
.logs-empty-cell{
  text-align: center;
  color: #6b7280;
  padding: 1.5em 0.75em;
  background: rgba(0, 0, 0, 0.02);
}
.log-resource-id{
  display: inline-block;
  max-width: 14ch;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
  font-family: monospace;
}
</style>

<script>
import {formatter} from '@/components/mixin/formatter.js'

export default{
  mixins: [formatter],
  props:['logs', 'fullMode', 'showInfo', 'showControls'],
  data(){
    return {
        selectedID: null,
        sortOrder: 'desc',
        operationType: '',
        search: '',
        debounceTimer: null
    }
  },
  computed: {
    // El backend responde null (slice nil de Go) cuando no hay resultados
    safeLogs: function () {
      return this.logs || [];
    },
    hasActiveFilters: function () {
      return this.operationType !== '' || this.search !== '' || this.sortOrder !== 'desc';
    },
    columnCount: function () {
      return this.fullMode ? 6 : 2;
    },
    emptyMessage: function () {
      return this.hasActiveFilters ? 'Sin resultados para los filtros aplicados' : 'No hay registros';
    },
    sortIcon: function () {
      //Mismo criterio que Users.vue: ícono neutral cuando rige el orden
      //natural (desc por fecha) y dirección solo con orden explícito
      return this.sortOrder === 'asc' ? 'sort-up' : 'sort';
    }
  },
  methods: {
    emitQuery: function () {
      this.$emit('queryChanged', {
        order: this.sortOrder,
        operationType: this.operationType,
        search: this.search
      });
    },
    onSortDate: function () {
      this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
      this.emitQuery();
    },
    onSearchInput: function () {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.emitQuery();
      }, 300);
    },
    clearFilters: function () {
      clearTimeout(this.debounceTimer);
      this.operationType = '';
      this.search = '';
      this.sortOrder = 'desc';
      this.emitQuery();
    },
    onClickRow: function (item) {
      if(!this.showInfo) {
        return;
      }
      this.onClickSelectItem(item);
    },
    onClickSelectItem: function (item) {
      this.selectedID = item.id;
      this.$emit('itemSelected', item);
    },
    isSelected: function (id) {
        if(this.selectedID == id) {
          return true;
        }
        return false;
    }
  },
  created(){

  },
  mounted(){

  },
  beforeDestroy(){
    clearTimeout(this.debounceTimer);
  },
  watch: {

  }
}
</script>
